import { useEffect, useRef, useState, useCallback, type FormEvent, type KeyboardEvent } from 'react'
import {
  Bot,
  Minimize2,
  Maximize2,
  X,
  Send,
  Paperclip,
  BarChart3,
  Globe,
  TrendingUp,
  Shield,
  Layers,
  User,
  Sparkles,
  PanelRightOpen,
  PanelRightClose,
} from 'lucide-react'
import clsx from 'clsx'
import axios from 'axios'
import api, { getAuthorizationHeaderValue } from '@/services/api'
import { useAIAgentStore, type AIMessage } from '@/store/aiAgentStore'

const AI_NOT_CONFIGURED_DETAIL =
  'Helios AI is not configured. Set `ANTHROPIC_API_KEY` on the API server (e.g. Azure App Settings / Key Vault), then restart the API.'
const AI_APPROVAL_REQUIRED_DETAIL =
  'External Anthropic inference is disabled in production pending written client approval. Configuring a key does not enable it.'

// ---------------------------------------------------------------------------
// Quick-action presets
// ---------------------------------------------------------------------------
const QUICK_ACTIONS = [
  { label: 'Analyze Deal', icon: BarChart3, prompt: 'Analyze the risk and return profile for the selected deal, including key credit metrics, market comps, and a go/no-go recommendation.' },
  { label: 'Risk Report', icon: Shield, prompt: 'Generate a comprehensive risk report for my current portfolio including concentration analysis, watchlist items, and stress test results.' },
  { label: 'Market Brief', icon: Globe, prompt: 'Provide a detailed market briefing covering CMBS spreads, issuance volumes, delinquency trends, and key themes for today.' },
  { label: 'Rate Analysis', icon: TrendingUp, prompt: 'Analyze current interest rate environment including SOFR curve, swap rates, and implications for our floating rate book.' },
  { label: 'Hedge Strategy', icon: Layers, prompt: 'What hedging strategies would you recommend for our current floating rate exposure across the portfolio?' },
  { label: 'Pipeline Summary', icon: Sparkles, prompt: 'Summarize the current deal pipeline including stage distribution, total volume, and any items requiring immediate attention.' },
]

// ---------------------------------------------------------------------------
// Simple markdown-ish renderer
// ---------------------------------------------------------------------------
function renderMessageContent(content: string) {
  const lines = content.split('\n')
  const elements: JSX.Element[] = []
  let inTable = false
  let tableRows: string[][] = []
  let tableKey = 0

  const flushTable = () => {
    if (tableRows.length < 2) {
      inTable = false
      tableRows = []
      return
    }
    const headers = tableRows[0]
    const dataRows = tableRows.slice(2) // skip separator row
    elements.push(
      <div key={`tbl-${tableKey++}`} className="my-2 overflow-x-auto rounded border border-amber-400/10">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="border-b border-amber-400/10 bg-black/30">
              {headers.map((h, i) => (
                <th key={i} className="px-2 py-1 text-left font-mono text-[9px] uppercase tracking-wider text-stone-500">
                  {renderInline(h.trim())}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dataRows.map((row, ri) => (
              <tr key={ri} className="border-b border-amber-400/[0.06] last:border-0">
                {row.map((cell, ci) => (
                  <td key={ci} className="px-2 py-1 text-stone-300">
                    {renderInline(cell.trim())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
    inTable = false
    tableRows = []
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    // Table rows
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      if (!inTable) inTable = true
      const cells = line.split('|').filter((_, idx, arr) => idx > 0 && idx < arr.length - 1)
      tableRows.push(cells)
      continue
    } else if (inTable) {
      flushTable()
    }

    // Empty line
    if (line.trim() === '') {
      elements.push(<div key={i} className="h-2" />)
      continue
    }

    // Headings
    if (line.startsWith('### ')) {
      elements.push(
        <h4 key={i} className="mt-3 mb-1 text-[11px] font-semibold uppercase tracking-wide text-amber-300/90">
          {renderInline(line.slice(4))}
        </h4>
      )
      continue
    }
    if (line.startsWith('## ')) {
      elements.push(
        <h3 key={i} className="mt-2 mb-1 text-[12px] font-bold text-stone-100">
          {renderInline(line.slice(3))}
        </h3>
      )
      continue
    }

    // Bullet points
    if (line.trim().startsWith('- ')) {
      elements.push(
        <div key={i} className="flex gap-1.5 py-0.5 pl-1">
          <span className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-amber-400/50" />
          <span className="text-[11.5px] leading-relaxed text-stone-300">{renderInline(line.trim().slice(2))}</span>
        </div>
      )
      continue
    }

    // Numbered list
    const numberedMatch = line.trim().match(/^(\d+)\.\s+(.*)/)
    if (numberedMatch) {
      elements.push(
        <div key={i} className="flex gap-2 py-0.5 pl-1">
          <span className="flex-shrink-0 font-mono text-[10px] text-amber-400/60">{numberedMatch[1]}.</span>
          <span className="text-[11.5px] leading-relaxed text-stone-300">{renderInline(numberedMatch[2])}</span>
        </div>
      )
      continue
    }

    // Regular paragraph
    elements.push(
      <p key={i} className="text-[11.5px] leading-relaxed text-stone-300">
        {renderInline(line)}
      </p>
    )
  }

  // Flush remaining table
  if (inTable) flushTable()

  return elements
}

function renderInline(text: string): (string | JSX.Element)[] {
  const parts: (string | JSX.Element)[] = []
  // Bold, then inline code, then emoji-style markers
  const regex = /(\*\*(.+?)\*\*)|(`([^`]+)`)/g
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }
    if (match[1]) {
      // Bold
      parts.push(
        <strong key={match.index} className="font-semibold text-stone-100">
          {match[2]}
        </strong>
      )
    } else if (match[3]) {
      // Inline code
      parts.push(
        <code key={match.index} className="rounded bg-black/40 px-1 py-0.5 font-mono text-[10px] text-amber-200">
          {match[4]}
        </code>
      )
    }
    lastIndex = match.index + match[0].length
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts
}

// ---------------------------------------------------------------------------
// Timestamp formatter
// ---------------------------------------------------------------------------
function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

// ---------------------------------------------------------------------------
// Message bubble
// ---------------------------------------------------------------------------
function MessageBubble({ message }: { message: AIMessage }) {
  const isAI = message.role === 'assistant'

  return (
    <div className={clsx('group flex gap-2.5 px-4 py-2', isAI ? 'items-start' : 'items-start')}>
      {/* Avatar */}
      <div
        className={clsx(
          'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg border text-xs',
          isAI
            ? 'border-amber-400/20 bg-gradient-to-br from-amber-400/10 to-amber-600/5 text-amber-300'
            : 'border-stone-700/50 bg-gradient-to-br from-stone-800 to-stone-900 text-stone-400'
        )}
      >
        {isAI ? <Bot size={14} /> : <User size={14} />}
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center gap-2">
          <span
            className={clsx(
              'text-[10px] font-semibold uppercase tracking-wider',
              isAI ? 'text-amber-300/80' : 'text-stone-400'
            )}
          >
            {isAI ? 'Helios AI' : 'You'}
          </span>
          <span className="text-[9px] text-stone-600">{formatTime(message.timestamp)}</span>
        </div>
        <div
          className={clsx(
            'rounded-lg border px-3 py-2.5',
            isAI
              ? 'border-l-2 border-amber-400/15 border-l-amber-400/40 bg-gradient-to-br from-[#101010] to-[#090909]'
              : 'border-stone-700/30 bg-stone-900/50'
          )}
        >
          {renderMessageContent(message.content)}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Typing indicator
// ---------------------------------------------------------------------------
function TypingIndicator() {
  return (
    <div className="flex gap-2.5 px-4 py-2">
      <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg border border-amber-400/20 bg-gradient-to-br from-amber-400/10 to-amber-600/5 text-amber-300">
        <Bot size={14} />
      </div>
      <div className="flex-1">
        <div className="mb-1 flex items-center gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-300/80">Helios AI</span>
        </div>
        <div className="inline-flex items-center gap-1 rounded-lg border border-l-2 border-amber-400/15 border-l-amber-400/40 bg-gradient-to-br from-[#101010] to-[#090909] px-4 py-3">
          <span className="ai-typing-dot h-1.5 w-1.5 rounded-full bg-amber-400/60" style={{ animationDelay: '0ms' }} />
          <span className="ai-typing-dot h-1.5 w-1.5 rounded-full bg-amber-400/60" style={{ animationDelay: '200ms' }} />
          <span className="ai-typing-dot h-1.5 w-1.5 rounded-full bg-amber-400/60" style={{ animationDelay: '400ms' }} />
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------
export default function AIAgentPanel() {
  const {
    isOpen,
    isMinimized,
    toggleMinimize,
    setOpen,
    isDocked,
    toggleDock,
    messages,
    addMessage,
    appendToLastMessage,
    inputValue,
    setInputValue,
    isTyping,
    setIsTyping,
  } = useAIAgentStore()

  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [aiConfigured, setAiConfigured] = useState<boolean | null>(null)
  const [aiBlockedReason, setAiBlockedReason] = useState<string | null>(null)
  const aiBlockedReasonRef = useRef<string | null>(null)
  const [aiStatusLoading, setAiStatusLoading] = useState(false)

  const loadAIStatus = useCallback(async (): Promise<boolean | null> => {
    setAiStatusLoading(true)
    try {
      const res = await api.get<{ claude_configured?: boolean; blocked_reason?: string | null }>(
        '/ai/status',
      )
      const configured = Boolean(res.data?.claude_configured)
      aiBlockedReasonRef.current = res.data?.blocked_reason ?? null
      setAiConfigured(configured)
      setAiBlockedReason(aiBlockedReasonRef.current)
      return configured
    } catch {
      setAiConfigured(null)
      aiBlockedReasonRef.current = null
      setAiBlockedReason(null)
      return null
    } finally {
      setAiStatusLoading(false)
    }
  }, [])

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages, isTyping])

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      void loadAIStatus()
      setTimeout(() => inputRef.current?.focus(), 300)
    }
  }, [isOpen, isMinimized, loadAIStatus])

  const handleSend = useCallback(async () => {
    const text = inputValue.trim()
    if (!text || isTyping) return

    let configured = aiConfigured
    if (configured !== true) {
      configured = configured === null ? await loadAIStatus() : configured
    }
    if (configured === false) {
      addMessage({
        role: 'assistant',
        content: `**Could not get a reply.** ${
          aiBlockedReasonRef.current === 'written_client_approval_required'
            ? AI_APPROVAL_REQUIRED_DETAIL
            : AI_NOT_CONFIGURED_DETAIL
        }`,
      })
      return
    }

    addMessage({ role: 'user', content: text })
    setInputValue('')
    setIsTyping(true)

    try {
      const thread = useAIAgentStore.getState().messages.map((m) => ({
        role: m.role,
        content: m.content,
      }))

      // Resolve base URL the same way the api service does
      const baseURL = (api.defaults?.baseURL ?? '/api/v1').replace(/\/$/, '')

      const authorization = await getAuthorizationHeaderValue()

      const response = await fetch(`${baseURL}/ai/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authorization ? { Authorization: authorization } : {}),
        },
        body: JSON.stringify({ messages: thread.slice(-32) }),
      })

      if (!response.ok) {
        // Fall back to reading error body
        const errBody = await response.json().catch(() => null)
        const detail = errBody?.detail ?? `Server error ${response.status}`
        if (typeof detail === 'string' && detail.includes('not configured')) {
          setAiConfigured(false)
        }
        if (typeof detail === 'string' && detail.includes('written client approval')) {
          setAiConfigured(false)
          aiBlockedReasonRef.current = 'written_client_approval_required'
          setAiBlockedReason('written_client_approval_required')
        }
        addMessage({
          role: 'assistant',
          content: `**Could not get a reply.** ${detail}`,
        })
        return
      }

      // Add empty assistant message and stream into it
      addMessage({ role: 'assistant', content: '' })

      const reader = response.body?.getReader()
      if (!reader) throw new Error('No response body')

      const decoder = new TextDecoder()
      let buffer = ''

      for (;;) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })

        // Parse SSE lines from buffer
        const lines = buffer.split('\n')
        buffer = lines.pop() ?? '' // keep incomplete line

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const payload = JSON.parse(line.slice(6))
            if (payload.type === 'delta' && payload.text) {
              appendToLastMessage(payload.text)
            } else if (payload.type === 'error') {
              appendToLastMessage(`\n\n**Error:** ${payload.text}`)
            }
            // 'done' type — stream complete
          } catch {
            // skip malformed lines
          }
        }
      }

      // If message is still empty after stream, show fallback
      const lastMsg = useAIAgentStore.getState().messages
      const last = lastMsg[lastMsg.length - 1]
      if (last?.role === 'assistant' && !last.content.trim()) {
        appendToLastMessage('(Empty response)')
      }
    } catch (err) {
      let detail = 'Could not reach Helios AI.'
      if (axios.isAxiosError(err)) {
        const d = err.response?.data
        if (typeof d?.detail === 'string') detail = d.detail
        else if (Array.isArray(d?.detail)) detail = d.detail.map((x: { msg?: string }) => x.msg).filter(Boolean).join('; ')
        if (detail.includes('Helios AI is not configured')) {
          setAiConfigured(false)
        }
      } else if (err instanceof Error) {
        detail = err.message
      }
      addMessage({
        role: 'assistant',
        content: `**Could not get a reply.** ${detail}`,
      })
    } finally {
      setIsTyping(false)
    }
  }, [inputValue, isTyping, aiConfigured, addMessage, appendToLastMessage, loadAIStatus, setInputValue, setIsTyping])

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    handleSend()
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleQuickAction = (prompt: string) => {
    if (aiConfigured === false) return
    setInputValue(prompt)
    setTimeout(() => inputRef.current?.focus(), 50)
  }

  const statusTone =
    aiConfigured === true
      ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-400'
      : aiConfigured === false
        ? 'border-amber-500/20 bg-amber-500/10 text-amber-300'
        : 'border-stone-500/20 bg-stone-500/10 text-stone-400'
  const statusLabel =
    aiConfigured === true
      ? 'Online'
      : aiBlockedReason === 'written_client_approval_required'
        ? 'Approval Required'
        : aiConfigured === false
          ? 'Not Configured'
          : aiStatusLoading
            ? 'Checking'
            : 'Unknown'
  const inputDisabled = isTyping || aiConfigured === false
  const placeholder =
    aiConfigured === false
      ? aiBlockedReason === 'written_client_approval_required'
        ? 'External AI is disabled pending written client approval.'
        : 'Helios AI is unavailable until ANTHROPIC_API_KEY is configured on the API.'
      : 'Ask about deals, markets, risk, structuring...'

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop overlay — only when NOT docked */}
      {!isDocked && (
        <div
          className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[1px] transition-opacity duration-300"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Panel */}
      <div
        className={clsx(
          'flex flex-col border-l border-amber-400/15 bg-gradient-to-b from-[#0b0b0b] via-[#080808] to-[#050505] transition-all duration-300 ease-out',
          isDocked
            ? 'h-full w-[400px] flex-shrink-0'
            : 'fixed right-0 top-0 z-50 h-full w-[420px] shadow-2xl'
        )}
        style={isDocked ? undefined : {
          animation: 'aiPanelSlideIn 300ms cubic-bezier(0.16, 1, 0.3, 1)',
          boxShadow: '-8px 0 40px rgba(0, 0, 0, 0.5), -2px 0 12px rgba(217, 183, 104, 0.04)',
        }}
      >
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex-shrink-0 border-b border-amber-400/15 bg-black/40 backdrop-blur-xl">
          <div className="flex items-center justify-between px-4 py-2.5">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-black/35 p-0.5 ring-1 ring-white/[0.06]">
                <img
                  src="/helios-white-logo-3x.png"
                  alt="Helios"
                  className="h-full w-full object-contain drop-shadow-[0_0_7px_rgba(117,100,203,0.28)]"
                />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-bold tracking-wide text-stone-100">HELIOS AI</span>
                  <span className={clsx('flex items-center gap-1 rounded-full border px-1.5 py-0.5', statusTone)}>
                    <span
                      className={clsx(
                        'h-1.5 w-1.5 rounded-full',
                        aiConfigured === true
                          ? 'bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.6)]'
                          : aiConfigured === false
                            ? 'bg-amber-300 shadow-[0_0_6px_rgba(251,191,36,0.35)]'
                            : 'bg-stone-400'
                      )}
                    />
                    <span className="text-[8px] font-semibold uppercase tracking-widest">{statusLabel}</span>
                  </span>
                </div>
                <div className="text-[10px] text-stone-500">CRE Finance & CMBS Intelligence</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={toggleDock}
                className={clsx(
                  'rounded-md p-1.5 transition-colors',
                  isDocked
                    ? 'bg-amber-400/10 text-amber-300 hover:bg-amber-400/15'
                    : 'text-stone-500 hover:bg-white/[0.04] hover:text-stone-300'
                )}
                title={isDocked ? 'Undock panel' : 'Dock panel'}
              >
                {isDocked ? <PanelRightClose size={13} /> : <PanelRightOpen size={13} />}
              </button>
              <button
                onClick={toggleMinimize}
                className="rounded-md p-1.5 text-stone-500 transition-colors hover:bg-white/[0.04] hover:text-stone-300"
                title={isMinimized ? 'Expand' : 'Minimize'}
              >
                {isMinimized ? <Maximize2 size={13} /> : <Minimize2 size={13} />}
              </button>
              <button
                onClick={() => setOpen(false)}
                className="rounded-md p-1.5 text-stone-500 transition-colors hover:bg-red-500/10 hover:text-red-400"
                title="Close"
              >
                <X size={13} />
              </button>
            </div>
          </div>
        </div>

        {/* ── Content (collapsible) ──────────────────────────────────────── */}
        {!isMinimized && (
          <>
            {/* Messages area */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto py-3 scrollbar-thin">
              {aiConfigured === false ? (
                <div className="mx-4 mb-3 rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-[11px] leading-relaxed text-amber-100">
                  <div className="font-semibold uppercase tracking-wide text-amber-300">
                    {aiBlockedReason === 'written_client_approval_required'
                      ? 'Client Approval Required'
                      : 'Configuration Required'}
                  </div>
                  <div className="mt-1 text-stone-300">
                    {aiBlockedReason === 'written_client_approval_required' ? (
                      AI_APPROVAL_REQUIRED_DETAIL
                    ) : (
                      <>
                        Set <code className="rounded bg-black/30 px-1 py-0.5 font-mono text-[10px] text-amber-200">ANTHROPIC_API_KEY</code> on the API server, then restart the API.
                      </>
                    )}
                  </div>
                </div>
              ) : null}
              {messages.map((msg) => (
                <MessageBubble key={msg.id} message={msg} />
              ))}
              {isTyping && <TypingIndicator />}
            </div>

            {/* Quick Actions */}
            <div className="flex-shrink-0 border-t border-amber-400/[0.08] bg-black/20 px-4 py-2">
              <div className="mb-1.5 text-[9px] font-mono uppercase tracking-[0.2em] text-stone-600">Quick Actions</div>
              <div className="flex flex-wrap gap-1.5">
                {QUICK_ACTIONS.map((action) => {
                  const Icon = action.icon
                  return (
                    <button
                      key={action.label}
                      onClick={() => handleQuickAction(action.prompt)}
                      disabled={aiConfigured === false}
                      className="flex items-center gap-1 rounded-md border border-amber-400/10 bg-amber-400/[0.03] px-2 py-1 text-[10px] font-medium text-stone-400 transition-all duration-150 hover:border-amber-400/25 hover:bg-amber-400/[0.07] hover:text-amber-200"
                    >
                      <Icon size={10} className="text-amber-400/50" />
                      {action.label}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* ── Input area ───────────────────────────────────────────────── */}
            <div className="flex-shrink-0 border-t border-amber-400/15 bg-black/30 px-4 py-3">
              <form onSubmit={handleSubmit} className="flex items-center gap-2">
                <button
                  type="button"
                  className="flex-shrink-0 rounded-md p-1.5 text-stone-600 transition-colors hover:text-stone-400"
                  title="Attach file"
                >
                  <Paperclip size={14} />
                </button>
                <div className="flex flex-1 items-center rounded-lg border border-amber-400/15 bg-black/50 px-3 py-2 transition-all duration-200 focus-within:border-amber-400/40 focus-within:shadow-[0_0_0_3px_rgba(196,154,67,0.08)]">
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    className="w-full bg-transparent text-[12px] text-stone-200 placeholder:text-stone-600 outline-none"
                    disabled={inputDisabled}
                  />
                </div>
                <button
                  type="submit"
                  disabled={!inputValue.trim() || inputDisabled}
                  className={clsx(
                    'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg transition-all duration-200',
                    inputValue.trim() && !inputDisabled
                      ? 'bg-gradient-to-br from-amber-400 to-amber-600 text-black shadow-[0_4px_12px_rgba(196,154,67,0.3)] hover:shadow-[0_6px_16px_rgba(196,154,67,0.4)]'
                      : 'bg-stone-800/50 text-stone-600'
                  )}
                >
                  <Send size={13} />
                </button>
              </form>
              <div className="mt-2 text-center text-[9px] text-stone-700">
                Helios AI may produce inaccurate information. Verify critical data independently.
              </div>
            </div>
          </>
        )}
      </div>
    </>
  )
}
